import type { ListenOptions, VoiceInput, VoiceOutput } from "./types.js";

interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult:
    ((e: { results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  onspeechstart: (() => void) | null;
}

type Ctor = new () => SpeechRecognitionLike;

function recognitionCtor(): Ctor | null {
  const w = window as unknown as { SpeechRecognition?: Ctor; webkitSpeechRecognition?: Ctor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Web Speech API (Chrome, Safari). Not available in Android WebView: the native plugin is used there. */
export class WebVoiceInput implements VoiceInput {
  readonly kind = "web";
  private rec: SpeechRecognitionLike | null = null;
  private finish: (() => void) | null = null;

  async available(): Promise<boolean> {
    return recognitionCtor() !== null;
  }

  listen(opts: ListenOptions): Promise<string | null> {
    const C = recognitionCtor();
    if (!C) return Promise.resolve(null);
    this.stop();
    return new Promise((resolve) => {
      const rec = new C();
      this.rec = rec;
      rec.lang = "pl-PL";
      rec.continuous = false;
      rec.interimResults = Boolean(opts.onPartial);
      rec.maxAlternatives = 1;
      let text = "";
      let started = false;
      let done = false;
      const end = (value: string | null) => {
        if (done) return;
        done = true;
        clearTimeout(silence);
        clearTimeout(cap);
        this.finish = null;
        rec.onresult = rec.onerror = rec.onend = rec.onspeechstart = null;
        try {
          rec.abort();
        } catch {
          /* already stopped */
        }
        if (this.rec === rec) this.rec = null;
        resolve(value && value.trim() ? value.trim() : null);
      };
      const silence = setTimeout(() => {
        if (!started) end(null);
      }, opts.timeoutMs);
      const cap = setTimeout(() => end(text || null), opts.maxMs ?? 20_000);
      rec.onspeechstart = () => {
        started = true;
      };
      rec.onresult = (e) => {
        let interim = "";
        let final = "";
        for (let i = 0; i < e.results.length; i++) {
          const r = e.results[i]!;
          if (r.isFinal) final += r[0]!.transcript;
          else interim += r[0]!.transcript;
        }
        started = true;
        if (interim) opts.onPartial?.(interim);
        if (final) {
          text = final;
          end(text);
        }
      };
      rec.onerror = () => end(text || null);
      rec.onend = () => end(text || null);
      this.finish = () => end(text || null);
      try {
        rec.start();
      } catch {
        end(null);
      }
    });
  }

  stop(): void {
    this.finish?.();
  }
}

/** speechSynthesis with a Polish system voice. */
export class WebVoiceOutput implements VoiceOutput {
  readonly kind = "web";
  private voiceName = "";

  private pick(): SpeechSynthesisVoice | null {
    const all = speechSynthesis.getVoices();
    return all.find((v) => v.name === this.voiceName) ?? all.find((v) => /^pl/i.test(v.lang)) ?? null;
  }

  speak(text: string): Promise<void> {
    if (typeof speechSynthesis === "undefined") return Promise.resolve();
    this.stop();
    return new Promise((resolve) => {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "pl-PL";
      const v = this.pick();
      if (v) u.voice = v;
      u.rate = 1.02;
      // Some engines never fire onend; cap by text length.
      const guard = setTimeout(() => resolve(), 2000 + text.length * 90);
      u.onend = u.onerror = () => {
        clearTimeout(guard);
        resolve();
      };
      speechSynthesis.speak(u);
    });
  }

  stop(): void {
    if (typeof speechSynthesis !== "undefined") speechSynthesis.cancel();
  }

  async voices(): Promise<string[]> {
    if (typeof speechSynthesis === "undefined") return [];
    let all = speechSynthesis.getVoices();
    if (!all.length)
      await new Promise<void>((r) => {
        const t = setTimeout(r, 1500);
        speechSynthesis.addEventListener("voiceschanged", () => (clearTimeout(t), r()), { once: true });
      });
    all = speechSynthesis.getVoices();
    return all.filter((v) => /^pl/i.test(v.lang)).map((v) => v.name);
  }

  setVoice(name: string): void {
    this.voiceName = name;
  }
}
